import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client/react";
import { ChoiceChips, Eyebrow, Heading, SearchInput, Skeleton, Text } from "@athena/ui";
import { ContactLogDocument } from "@/graphql/clients.generated";
import { useContactChanges } from "@/hooks/useContactChanges";
import { formatLogDay, groupByDay, relativeDay } from "./contactLogDays";
import { ContactLogSkeleton } from "./ContactLogSkeleton";
import { ContactLogDialog, type ContactLogDialogEntry } from "./components/ContactLogDialog";
import { ContactLogItem } from "./components/ContactLogItem";
import {
    CONTACT_CHANNELS,
    CONTACT_TYPE_FOR_CHANNEL,
    toClientRecord,
    toContactEntry,
    type ContactChannel,
} from "./helpers/client";

/** The API's largest page. The log does not page yet; past this it says how many it is not showing. */
const CONTACT_LOG_PAGE_LIMIT = 200;

type ChannelFilter = "all" | ContactChannel;

/** Every contact type, chat included: a hand-logged chat is in the log like any other contact. */
const CHANNEL_FILTERS = [
    "all",
    ...CONTACT_CHANNELS,
    "chat",
] as const satisfies readonly ChannelFilter[];

export function ContactLogPage() {
    const { t } = useTranslation();
    const ids = useId();
    const today = useMemo(() => new Date(), []);
    const [
        filter,
        setFilter,
    ] = useState<ChannelFilter>("all");
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const trimmedSearch = search.trim();
    const { data, previousData, loading, error } = useQuery(ContactLogDocument, {
        variables: {
            limit: CONTACT_LOG_PAGE_LIMIT,
            ...(filter !== "all" && { type: CONTACT_TYPE_FOR_CHANNEL[filter] }),
            ...(trimmedSearch && { search: trimmedSearch }),
        },
    });

    // Keep showing the last answer while a new search or filter loads, rather than flashing empty.
    const log = (data ?? previousData)?.contactLog;
    const entries = useMemo(
        () =>
            (log?.entries ?? []).map((entry) => ({
                contact: toContactEntry(entry.contact),
                client: toClientRecord(entry.client),
            })),
        [log],
    );
    const days = useMemo(() => groupByDay(entries), [entries]);

    // Kept after the dialog closes, so its text does not change while it animates away.
    const [
        openEntry,
        setOpenEntry,
    ] = useState<ContactLogDialogEntry | undefined>();
    const [
        dialogOpen,
        setDialogOpen,
    ] = useState<boolean>(false);

    // The contact as the log has it now, so an edit shows once the log is re-read. The copy taken on
    // opening is only for once it has gone — deleted, while the dialog animates away.
    const shownEntry = entries.find((entry) => entry.contact.id === openEntry?.contact.id) ?? openEntry;
    const { saveContact, deleteContact } = useContactChanges();

    const dayHeading = (day: string) => {
        const date = formatLogDay(day, today);
        const relative = relativeDay(day, today);
        return relative ? t(`admin.alumni.contactLog.${relative}`, { date }) : date;
    };

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10 md:py-8">
            <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-2xl">
                    <Heading level={2}>{t("admin.alumni.contactLog.title")}</Heading>
                    <Text muted className="mt-2">
                        {t("admin.alumni.contactLog.description")}
                    </Text>
                </div>
            </header>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
                <div className="w-full shrink-0 lg:w-92">
                    <SearchInput
                        label={t("admin.alumni.contactLog.searchLabel")}
                        placeholder={t("admin.alumni.contactLog.searchPlaceholder")}
                        className="bg-card h-10 rounded-full"
                        value={search}
                        onValueChange={setSearch}
                    />
                </div>
                <ChoiceChips
                    aria-label={t("admin.alumni.queue.filterLabel")}
                    value={filter}
                    onValueChange={(value) => setFilter(value as ChannelFilter)}
                    options={CHANNEL_FILTERS.map((key) => ({
                        value: key,
                        label:
                            key === "all" ? t("admin.alumni.contactLog.allChannels") : t(`admin.alumni.channel.${key}`),
                    }))}
                />
            </div>
            {log ? (
                <Text className="text-muted-foreground mt-2 font-mono">
                    {t("admin.alumni.contactLog.total", { count: log.total })}
                </Text>
            ) : loading ? (
                <Skeleton className="mt-3 h-5 w-56" />
            ) : null}

            {!log && loading ? (
                <ContactLogSkeleton />
            ) : error && !log ? (
                <Text size="sm" muted className="py-16 text-center">
                    {t("admin.alumni.contactLog.loadFailed")}
                </Text>
            ) : entries.length === 0 ? (
                <Text size="sm" muted className="py-16 text-center">
                    {trimmedSearch
                        ? t("admin.alumni.contactLog.noMatches", { search: trimmedSearch })
                        : filter === "all"
                          ? t("admin.alumni.contactLog.empty")
                          : t("admin.alumni.contactLog.emptyForChannel", {
                                channel: t(`admin.alumni.channel.${filter}`),
                            })}
                </Text>
            ) : (
                <>
                    <div className="grid gap-8">
                        {days.map(({ day, entries: dayEntries }) => (
                            <section key={day} aria-labelledby={`${ids}-${day}`}>
                                <div className="mb-3 flex items-center gap-3">
                                    <h3 id={`${ids}-${day}`} className="flex items-baseline gap-2">
                                        <Eyebrow as="span">{dayHeading(day)}</Eyebrow>
                                        <Eyebrow as="span" className="text-text-caption" aria-hidden>
                                            ({dayEntries.length})
                                        </Eyebrow>
                                        {/* the span is for screen readers */}
                                        <span className="sr-only">
                                            {`, ${t("admin.alumni.contactLog.dayCount", { count: dayEntries.length })}`}
                                        </span>
                                    </h3>
                                    <div className="bg-border h-px flex-1" aria-hidden />
                                </div>
                                <ul aria-labelledby={`${ids}-${day}`} className="grid gap-3">
                                    {dayEntries.map(({ contact, client }) => (
                                        <li key={contact.id}>
                                            <ContactLogItem
                                                client={client}
                                                contact={contact}
                                                onOpen={() => {
                                                    setOpenEntry({ contact, client });
                                                    setDialogOpen(true);
                                                }}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                    {log && log.total > entries.length ? (
                        <Text size="sm" muted>
                            {t("admin.alumni.contactLog.moreNotShown", { shown: entries.length, total: log.total })}
                        </Text>
                    ) : null}
                </>
            )}

            <ContactLogDialog
                entry={shownEntry}
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                today={today}
                onSave={saveContact}
                onDelete={deleteContact}
            />
        </div>
    );
}
